import React, { useState, useEffect } from 'react';
import { Chart, registerables } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { fetchCES4Data, CES4_INDICATOR_MAP, CES4Data } from '../../services/ces4DataService';
import { supabaseClient } from '../../services/supabase';

Chart.register(...registerables);

interface BurdenBenefitsChartProps {
  type: 'burdens' | 'benefits';
  housingBurdenPercentile?: number | null;
  onPercentileCalculated?: (percentile: number | null) => void;
  onScoreYearCalculated?: (year: number | null) => void;
}

interface Category {
  name: string;
  percentile: number | null; // null when no data loaded yet
  expanded: boolean;
  indicators: {
    name: string;
    percentile: number | null; // null = loading or "coming soon"
    year?: number | null;
  }[];
}

export const BurdenBenefitsChart: React.FC<BurdenBenefitsChartProps> = ({ type, housingBurdenPercentile, onPercentileCalculated, onScoreYearCalculated }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  // Log when selectedGeofips changes to track potential issues
  useEffect(() => {
    if (selectedLocation && selectedGeofips) {
      console.log(`🔍 BurdenBenefitsChart - selectedGeofips changed:`, {
        location: selectedLocation,
        geofips: selectedGeofips,
        timestamp: new Date().toISOString()
      });
    }
  }, [selectedLocation, selectedGeofips]);
  const [ces4Data, setCes4Data] = useState<CES4Data | null>(null);
  const [ghgPercentiles, setGhgPercentiles] = useState<{
    ldv?: number;
    hdv?: number;
    electricity?: number;
    naturalGas?: number;
    waste?: number;
    offroad?: number;
    total?: number;
  } | null>(null);
  const [commuteTimePercentile, setCommuteTimePercentile] = useState<number | null>(null);
  const [evPer1000Percentile, setEvPer1000Percentile] = useState<number | null>(null);
  const [evChargingPercentile, setEvChargingPercentile] = useState<number | null>(null);
  const [solarPVPercentile, setSolarPVPercentile] = useState<number | null>(null);
  const [heatPumpPercentile, setHeatPumpPercentile] = useState<number | null>(null);
  const [treeCanopyPercentile, setTreeCanopyPercentile] = useState<number | null>(null);
  const [educationalAttainmentPercentile, setEducationalAttainmentPercentile] = useState<number | null>(null);
  const [healthInsurancePercentile, setHealthInsurancePercentile] = useState<number | null>(null);
  const [racialNonWhitePercentile, setRacialNonWhitePercentile] = useState<number | null>(null);
  const [ghgYear, setGhgYear] = useState<number | null>(null);
  const [commuteYear, setCommuteYear] = useState<number | null>(null);
  const [evPer1000Year, setEvPer1000Year] = useState<number | null>(null);
  const [evChargingYear, setEvChargingYear] = useState<number | null>(null);
  const [solarPVYear, setSolarPVYear] = useState<number | null>(null);
  const [heatPumpYear, setHeatPumpYear] = useState<number | null>(null);
  const [treeCanopyYear, setTreeCanopyYear] = useState<number | null>(null);
  const [educationalYear, setEducationalYear] = useState<number | null>(null);
  const [healthInsuranceYear, setHealthInsuranceYear] = useState<number | null>(null);
  const [racialNonWhiteYear, setRacialNonWhiteYear] = useState<number | null>(null);
  const [equityYears, setEquityYears] = useState<{
    incomeEquity?: number | null;
    educationEquity?: number | null;
    wageEquity?: number | null;
    medianIncome?: number | null;
    jobWage?: number | null;
  } | null>(null);
  const [medianIncomePercentile, setMedianIncomePercentile] = useState<number | null>(null);
  const [jobWagePercentile, setJobWagePercentile] = useState<number | null>(null);
  const [equityPercentiles, setEquityPercentiles] = useState<{
    incomeEquity?: number;
    educationEquity?: number;
    wageEquity?: number;
  } | null>(null);

  // Track which data sources have finished loading (so we can show skeleton vs "Coming soon")
  const [sourcesLoaded, setSourcesLoaded] = useState<{
    ghg: boolean;
    ces4: boolean;
    commute: boolean;
    evPer1000: boolean;
    evCharging: boolean;
    solarPV: boolean;
    heatPump: boolean;
    treeCanopy: boolean;
    educational: boolean;
    equity: boolean;
    healthInsurance: boolean;
    racialNonWhite: boolean;
  }>({
    ghg: false,
    ces4: false,
    commute: false,
    evPer1000: false,
    evCharging: false,
    solarPV: false,
    heatPump: false,
    treeCanopy: false,
    educational: false,
    equity: false,
    healthInsurance: false,
    racialNonWhite: false,
  });

  const setSourceLoaded = (key: keyof typeof sourcesLoaded) => {
    setSourcesLoaded((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  // Reset load flags when location/geofips changes
  useEffect(() => {
    setSourcesLoaded({
      ghg: false,
      ces4: false,
      commute: false,
      evPer1000: false,
      evCharging: false,
      solarPV: false,
      heatPump: false,
      treeCanopy: false,
      educational: false,
      equity: false,
      healthInsurance: false,
      racialNonWhite: false,
    });
  }, [selectedLocation, selectedGeofips]);

  // Fetch CES4 data when location changes
  useEffect(() => {
    const loadCES4Data = async () => {
      if (!selectedLocation) {
        setCes4Data(null);
        return;
      }

      try {
        const data = await fetchCES4Data(selectedLocation);
        setCes4Data(data);
      } catch (error) {
        console.error('Error loading CES4 data:', error);
        setCes4Data(null);
      } finally {
        setSourceLoaded('ces4');
      }
    };

    loadCES4Data();
  }, [selectedLocation]);

  // Fetch GHG emissions percentiles from ghg_inventory_precalculated
  useEffect(() => {
    const loadGHGPercentiles = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setGhgPercentiles(null);
        setGhgYear(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2023)
        const { data, error } = await supabaseClient
          .from('ghg_inventory_precalculated')
          .select('year, ldv_emissions_percentile, hdv_emissions_percentile, elec_total_emissions_percentile, ng_total_emissions_percentile, waste_emissions_percentile, offroad_emissions_percentile, total_emissions_percentile')
          .eq('geofips', selectedGeofips)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading GHG percentiles:', error);
          setGhgPercentiles(null);
          return;
        }

        if (data && data.length > 0) {
          const latest = data[0];
          setGhgYear(typeof latest.year === 'number' ? latest.year : Number(latest.year) || null);
          setGhgPercentiles({
            ldv: latest.ldv_emissions_percentile ? Math.round(latest.ldv_emissions_percentile) : undefined,
            hdv: latest.hdv_emissions_percentile ? Math.round(latest.hdv_emissions_percentile) : undefined,
            electricity: latest.elec_total_emissions_percentile ? Math.round(latest.elec_total_emissions_percentile) : undefined,
            naturalGas: latest.ng_total_emissions_percentile ? Math.round(latest.ng_total_emissions_percentile) : undefined,
            waste: latest.waste_emissions_percentile ? Math.round(latest.waste_emissions_percentile) : undefined,
            offroad: latest.offroad_emissions_percentile ? Math.round(latest.offroad_emissions_percentile) : undefined,
            total: latest.total_emissions_percentile ? Math.round(latest.total_emissions_percentile) : undefined,
          });
        } else {
          setGhgPercentiles(null);
          setGhgYear(null);
        }
      } catch (error) {
        console.error('Error loading GHG percentiles:', error);
        setGhgPercentiles(null);
        setGhgYear(null);
      } finally {
        setSourceLoaded('ghg');
      }
    };

    loadGHGPercentiles();
  }, [selectedLocation, selectedGeofips]);

  // Fetch commute time percentile
  useEffect(() => {
    const loadCommuteTimePercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setCommuteTimePercentile(null);
        setCommuteYear(null);
        return;
      }

      try {
        const { data, error } = await supabaseClient
          .from('commute_time')
          .select('year, percentile')
          .eq('geofips', String(selectedGeofips))
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading commute time percentile:', error);
          setCommuteTimePercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setCommuteTimePercentile(data[0].percentile ? Math.round(data[0].percentile) : null);
          setCommuteYear(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
        } else {
          setCommuteTimePercentile(null);
          setCommuteYear(null);
        }
      } catch (error) {
        console.error('Error loading commute time percentile:', error);
        setCommuteTimePercentile(null);
        setCommuteYear(null);
      } finally {
        setSourceLoaded('commute');
      }
    };

    loadCommuteTimePercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch EV per 1000 people percentile from ghg_inventory_precalculated
  useEffect(() => {
    const loadEVPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEvPer1000Percentile(null);
        setEvPer1000Year(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2023)
        const { data, error } = await supabaseClient
          .from('ghg_inventory_precalculated')
          .select('year, ev_per_1000_people_percentile')
          .eq('geofips', selectedGeofips)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading EV percentile:', error);
          setEvPer1000Percentile(null);
          return;
        }

        if (data && data.length > 0) {
          setEvPer1000Percentile(data[0].ev_per_1000_people_percentile ? Math.round(data[0].ev_per_1000_people_percentile) : null);
          setEvPer1000Year(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
        } else {
          setEvPer1000Percentile(null);
          setEvPer1000Year(null);
        }
      } catch (error) {
        console.error('Error loading EV percentile:', error);
        setEvPer1000Percentile(null);
        setEvPer1000Year(null);
      } finally {
        setSourceLoaded('evPer1000');
      }
    };

    loadEVPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch EV Charging percentile from ev_chargers table
  useEffect(() => {
    const loadEVChargingPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEvChargingPercentile(null);
        setEvChargingYear(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2024)
        const { data, error } = await supabaseClient
          .from('ev_chargers')
          .select('year, chargers_per_1000_residents_percentile')
          .eq('geofips', selectedGeofips)
          .not('chargers_per_1000_residents_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading EV Charging percentile:', error);
          setEvChargingPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setEvChargingPercentile(data[0].chargers_per_1000_residents_percentile ? Math.round(data[0].chargers_per_1000_residents_percentile) : null);
          setEvChargingYear(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
        } else {
          setEvChargingPercentile(null);
          setEvChargingYear(null);
        }
      } catch (error) {
        console.error('Error loading EV Charging percentile:', error);
        setEvChargingPercentile(null);
        setEvChargingYear(null);
      } finally {
        setSourceLoaded('evCharging');
      }
    };

    loadEVChargingPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Solar PV per capita percentile from solar_pv_per_capita view
  useEffect(() => {
    const loadSolarPVPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setSolarPVPercentile(null);
        setSolarPVYear(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2023)
        const { data, error } = await supabaseClient
          .from('solar_pv_per_capita')
          .select('year, capacity_per_capita_percentile')
          .eq('geoid', selectedGeofips)
          .not('capacity_per_capita_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Solar PV percentile:', error);
          setSolarPVPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setSolarPVPercentile(data[0].capacity_per_capita_percentile ? Math.round(data[0].capacity_per_capita_percentile) : null);
          setSolarPVYear(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
        } else {
          setSolarPVPercentile(null);
          setSolarPVYear(null);
        }
      } catch (error) {
        console.error('Error loading Solar PV percentile:', error);
        setSolarPVPercentile(null);
        setSolarPVYear(null);
      } finally {
        setSourceLoaded('solarPV');
      }
    };

    loadSolarPVPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Heat Pump per 10,000 residents percentile from heat_pump_per_capita view
  useEffect(() => {
    const loadHeatPumpPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setHeatPumpPercentile(null);
        setHeatPumpYear(null);
        return;
      }

      try {
        // Get the latest year's data
        const { data, error } = await supabaseClient
          .from('heat_pump_per_capita')
          .select('year, units_per_10000_percentile')
          .eq('geoid', selectedGeofips)
          .not('units_per_10000_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Heat Pump percentile:', error);
          setHeatPumpPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setHeatPumpPercentile(data[0].units_per_10000_percentile ? Math.round(data[0].units_per_10000_percentile) : null);
          setHeatPumpYear(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
        } else {
          setHeatPumpPercentile(null);
          setHeatPumpYear(null);
        }
      } catch (error) {
        console.error('Error loading Heat Pump percentile:', error);
        setHeatPumpPercentile(null);
        setHeatPumpYear(null);
      } finally {
        setSourceLoaded('heatPump');
      }
    };

    loadHeatPumpPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Educational Attainment percentile from educational_attainment_percentiles view
  useEffect(() => {
    const loadEducationalAttainmentPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEducationalAttainmentPercentile(null);
        setEducationalYear(null);
        return;
      }

      try {
        // Get the latest year's data
        const { data, error } = await supabaseClient
          .from('educational_attainment_percentiles')
          .select('year, percent_bachelors_percentile')
          .eq('geofips', selectedGeofips)
          .not('percent_bachelors_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Educational Attainment percentile:', error);
          setEducationalAttainmentPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setEducationalAttainmentPercentile(data[0].percent_bachelors_percentile ? Math.round(data[0].percent_bachelors_percentile) : null);
          setEducationalYear(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
        } else {
          setEducationalAttainmentPercentile(null);
          setEducationalYear(null);
        }
      } catch (error) {
        console.error('Error loading Educational Attainment percentile:', error);
        setEducationalAttainmentPercentile(null);
        setEducationalYear(null);
      } finally {
        setSourceLoaded('educational');
      }
    };

    loadEducationalAttainmentPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Tree Canopy Coverage percentile from tree_canopy_coverage table
  useEffect(() => {
    const loadTreeCanopyPercentile = async () => {
      if (!selectedGeofips) {
        setTreeCanopyPercentile(null);
        setTreeCanopyYear(null);
        return;
      }

      try {
        const geofipsInt = Number(selectedGeofips);
        if (!Number.isFinite(geofipsInt)) {
          setTreeCanopyPercentile(null);
          return;
        }

        const toDisplayPercentile = (value: number | null | undefined): number | null => {
          if (value === null || value === undefined || !Number.isFinite(value)) return null;
          const scaled = value <= 1 ? value * 100 : value;
          return Math.round(scaled);
        };

        const { data, error } = await supabaseClient
          .from('tree_canopy_coverage')
          .select('year, percentile')
          .eq('geofips', geofipsInt)
          .not('percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Tree Canopy percentile:', error);
          setTreeCanopyPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setTreeCanopyPercentile(toDisplayPercentile(data[0].percentile));
          setTreeCanopyYear(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
          return;
        }

        // Fallback for city selections when canopy table only has county-level rows.
        const countyName = selectedLocation ? supabaseLocationService.getCountyForCity(selectedLocation) : null;
        if (!countyName) {
          setTreeCanopyPercentile(null);
          setTreeCanopyYear(null);
          return;
        }

        const { data: countyData, error: countyError } = await supabaseClient
          .from('tree_canopy_coverage')
          .select('year, percentile')
          .eq('location_name', countyName)
          .eq('geography_type', 'county')
          .not('percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (countyError) {
          console.error('Error loading county fallback Tree Canopy percentile:', countyError);
          setTreeCanopyPercentile(null);
          return;
        }

        if (countyData && countyData.length > 0) {
          setTreeCanopyPercentile(toDisplayPercentile(countyData[0].percentile));
          setTreeCanopyYear(typeof countyData[0].year === 'number' ? countyData[0].year : Number(countyData[0].year) || null);
        } else {
          setTreeCanopyPercentile(null);
          setTreeCanopyYear(null);
        }
      } catch (error) {
        console.error('Error loading Tree Canopy percentile:', error);
        setTreeCanopyPercentile(null);
        setTreeCanopyYear(null);
      } finally {
        setSourceLoaded('treeCanopy');
      }
    };

    loadTreeCanopyPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Equity Indicators percentiles from equity_indicators_summary table
  useEffect(() => {
    const loadEquityPercentiles = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEquityPercentiles(null);
        setMedianIncomePercentile(null);
        setJobWagePercentile(null);
        setEquityYears(null);
        return;
      }

      try {
        // Get the latest year's data
        // Convert geofips to string and pad with leading zeros to match database format
        // Database stores geofips as TEXT with leading zeros (e.g., "0668154" not "668154")
        if (!selectedGeofips || selectedGeofips === 0) {
          setEquityPercentiles(null);
          setEquityYears(null);
          return;
        }
        
        // Validate that Berkeley's geofips (606000) is never used for any location other than Berkeley
        // This prevents Berkeley from being used as a fallback for any location
        const locationLower = selectedLocation.toLowerCase();
        const isBerkeley = locationLower.includes('berkeley');
        const isBerkeleyGeofips = selectedGeofips === 606000;
        
        if (isBerkeleyGeofips && !isBerkeley) {
          console.error(`❌ BurdenBenefitsChart - BLOCKED: Berkeley geofips (606000) detected for non-Berkeley location "${selectedLocation}". Berkeley should never be used as a fallback. Skipping query to prevent wrong data.`);
          setEquityPercentiles(null);
          setEquityYears(null);
          return;
        }
        
        const selectedGeofipsStr = String(selectedGeofips);
        let geofipsStr = selectedGeofipsStr;
        // Pad to 7 digits for cities (place), 5 for counties, 2 for state
        // Only pad if it's a valid numeric string
        if (/^\d+$/.test(geofipsStr)) {
          if (geofipsStr.length === 6) {
            // City geofips should be 7 digits with leading zero
            geofipsStr = '0' + geofipsStr;
          } else if (geofipsStr.length === 4) {
            // County geofips should be 5 digits with leading zero
            geofipsStr = '0' + geofipsStr;
          } else if (geofipsStr.length === 1) {
            // State geofips should be 2 digits with leading zero (e.g., "06" for California)
            geofipsStr = '0' + geofipsStr;
          }
        }

        // Some tables contain both padded and unpadded geofips variants for the same location.
        // Query all likely variants and let downstream filtering pick the latest valid row.
        const geofipsCandidates = Array.from(new Set([
          selectedGeofipsStr,
          geofipsStr,
          selectedGeofipsStr.replace(/^0+/, ''),
          selectedGeofipsStr.padStart(5, '0'),
          selectedGeofipsStr.padStart(7, '0')
        ].filter(Boolean)));
        
        console.log('🔍 BurdenBenefitsChart - Loading equity percentiles:', {
          location: selectedLocation,
          geofips: selectedGeofips,
          geofipsStr: geofipsStr,
          geofipsCandidates
        });
        
        const { data, error } = await supabaseClient
          .from('equity_indicators_summary')
          .select('geofips, year, income_equity_percentile, education_equity_percentile, wage_equity_percentile, median_household_income_percentile, job_wage_percentile')
          .in('geofips', geofipsCandidates)
          .or('income_equity_percentile.not.is.null,education_equity_percentile.not.is.null,wage_equity_percentile.not.is.null,median_household_income_percentile.not.is.null,job_wage_percentile.not.is.null')
          .order('year', { ascending: false })
          .limit(100);

        if (error) {
          console.error('❌ Error loading Equity percentiles:', error);
          // Log more details about the error
          if (error.message) {
            console.error('Error message:', error.message);
          }
          if (error.details) {
            console.error('Error details:', error.details);
          }
          if (error.hint) {
            console.error('Error hint:', error.hint);
          }
          setEquityPercentiles(null);
          return;
        }

        console.log('🔍 BurdenBenefitsChart - Query result:', {
          location: selectedLocation,
          geofips: geofipsStr,
          dataFound: data ? data.length : 0,
          data: data
        });

        if (data && data.length > 0) {
          const rows = data;
          const toPercentile = (value: number | null | undefined) =>
            value !== null && value !== undefined ? Math.round(value) : undefined;

          const pickLatestNonNull = <T extends keyof (typeof rows)[number]>(field: T) => {
            const row = rows.find((r: any) => r[field] !== null && r[field] !== undefined);
            return row ? { value: row[field], year: row.year, geofips: row.geofips } : null;
          };

          const incomeEquityMatch = pickLatestNonNull('income_equity_percentile');
          const educationEquityMatch = pickLatestNonNull('education_equity_percentile');
          const wageEquityMatch = pickLatestNonNull('wage_equity_percentile');
          const medianIncomeMatch = pickLatestNonNull('median_household_income_percentile');
          const jobWageMatch = pickLatestNonNull('job_wage_percentile');

          // Equity percentiles are now standardized in the database as "higher = better".
          const incomeEquity = toPercentile(incomeEquityMatch?.value as number | null | undefined);
          const educationEquity = toPercentile(educationEquityMatch?.value as number | null | undefined);
          const wageEquity = toPercentile(wageEquityMatch?.value as number | null | undefined);

          console.log('🔍 BurdenBenefitsChart - Equity Percentiles:', {
            location: selectedLocation,
            geofips: selectedGeofips,
            sources: {
              income: incomeEquityMatch ? `${incomeEquityMatch.geofips} (${incomeEquityMatch.year})` : null,
              education: educationEquityMatch ? `${educationEquityMatch.geofips} (${educationEquityMatch.year})` : null,
              wage: wageEquityMatch ? `${wageEquityMatch.geofips} (${wageEquityMatch.year})` : null,
              medianIncome: medianIncomeMatch ? `${medianIncomeMatch.geofips} (${medianIncomeMatch.year})` : null,
              jobWage: jobWageMatch ? `${jobWageMatch.geofips} (${jobWageMatch.year})` : null
            },
            income: incomeEquity,
            education: educationEquity,
            wage: wageEquity
          });

          setEquityPercentiles({
            incomeEquity,
            educationEquity,
            wageEquity,
          });
          setEquityYears({
            incomeEquity: incomeEquityMatch?.year ?? null,
            educationEquity: educationEquityMatch?.year ?? null,
            wageEquity: wageEquityMatch?.year ?? null,
            medianIncome: medianIncomeMatch?.year ?? null,
            jobWage: jobWageMatch?.year ?? null
          });
          setMedianIncomePercentile(
            medianIncomeMatch && medianIncomeMatch.value !== null && medianIncomeMatch.value !== undefined
              ? Math.round(medianIncomeMatch.value as number)
              : null
          );
          setJobWagePercentile(
            jobWageMatch && jobWageMatch.value !== null && jobWageMatch.value !== undefined
              ? Math.round(jobWageMatch.value as number)
              : null
          );
        } else {
          console.warn('⚠️ BurdenBenefitsChart - No equity percentile data found for:', {
            location: selectedLocation,
            geofips: geofipsStr
          });
          setEquityPercentiles(null);
          setMedianIncomePercentile(null);
          setJobWagePercentile(null);
          setEquityYears(null);
        }
      } catch (err) {
        console.error('Error loading Equity percentiles:', err);
        // Log more details about the error
        if (err instanceof Error) {
          console.error('Error name:', err.name);
          console.error('Error message:', err.message);
          console.error('Error stack:', err.stack);
        } else if (typeof err === 'object' && err !== null) {
          console.error('Error object:', JSON.stringify(err, null, 2));
        }
        setEquityPercentiles(null);
        setMedianIncomePercentile(null);
        setJobWagePercentile(null);
        setEquityYears(null);
      } finally {
        setSourceLoaded('equity');
      }
    };

    loadEquityPercentiles();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Health Insurance percentile from health_insurance_percentiles view
  useEffect(() => {
    const loadHealthInsurancePercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setHealthInsurancePercentile(null);
        setHealthInsuranceYear(null);
        return;
      }

      try {
        // Get the latest year's data
        const { data, error } = await supabaseClient
          .from('health_insurance_percentiles')
          .select('year, percent_with_insurance_percentile')
          .eq('geofips', selectedGeofips)
          .not('percent_with_insurance_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Health Insurance percentile:', error);
          setHealthInsurancePercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setHealthInsurancePercentile(data[0].percent_with_insurance_percentile ? Math.round(data[0].percent_with_insurance_percentile) : null);
          setHealthInsuranceYear(typeof data[0].year === 'number' ? data[0].year : Number(data[0].year) || null);
        } else {
          setHealthInsurancePercentile(null);
          setHealthInsuranceYear(null);
        }
      } catch (error) {
        console.error('Error loading Health Insurance percentile:', error);
        setHealthInsurancePercentile(null);
        setHealthInsuranceYear(null);
      } finally {
        setSourceLoaded('healthInsurance');
      }
    };

    loadHealthInsurancePercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch racial equity percentile from materialized view (place-level for cities, county-level for counties).
  useEffect(() => {
    const loadRacialNonWhitePercentile = async () => {
      if (!selectedGeofips || !selectedLocation) {
        setRacialNonWhitePercentile(null);
        setRacialNonWhiteYear(null);
        return;
      }

      try {
        const geofipsNum = Number(selectedGeofips);
        if (!Number.isFinite(geofipsNum)) {
          setRacialNonWhitePercentile(null);
          setRacialNonWhiteYear(null);
          return;
        }

        // Resolve geography_type for this geofips (City, County, or State).
        const { data: popRow, error: popError } = await supabaseClient
          .from('population')
          .select('geography_type')
          .eq('geofips', geofipsNum)
          .limit(1)
          .maybeSingle();

        if (popError || !popRow?.geography_type) {
          setRacialNonWhitePercentile(null);
          setRacialNonWhiteYear(null);
          return;
        }

        const geographyType = String(popRow.geography_type).trim();
        // View has City and County only; State has no row.
        if (geographyType === 'State') {
          setRacialNonWhitePercentile(null);
          setRacialNonWhiteYear(null);
          return;
        }

        // View uses 'City' or 'Place' for cities; match both when selection is a city.
        const viewGeoTypes = geographyType === 'County' ? ['County'] : ['City', 'Place'];

        const { data: rows, error } = await supabaseClient
          .from('racial_equity_percentile_place_county')
          .select('year, racial_equity_percentile')
          .eq('geofips', geofipsNum)
          .in('geography_type', viewGeoTypes)
          .order('year', { ascending: false })
          .limit(1);

        if (error || !rows?.length) {
          setRacialNonWhitePercentile(null);
          setRacialNonWhiteYear(rows?.[0]?.year ?? null);
          return;
        }

        const row = rows[0] as { year: number; racial_equity_percentile: number | null };
        const pct = row.racial_equity_percentile;
        setRacialNonWhitePercentile(pct != null ? Math.round(pct) : null);
        setRacialNonWhiteYear(row.year ?? null);
      } catch (err) {
        console.error('Error loading racial non-white percentile:', err);
        setRacialNonWhitePercentile(null);
        setRacialNonWhiteYear(null);
      } finally {
        setSourceLoaded('racialNonWhite');
      }
    };

    loadRacialNonWhitePercentile();
  }, [selectedLocation, selectedGeofips]);

  // Helper function to calculate average percentile from indicators
  // Returns null when no data exists (so we can show loading bar instead of 50)
  const calculateCategoryPercentile = (indicators: { name: string; percentile: number | null; year?: number | null }[]): number | null => {
    if (indicators.length === 0) return null;
    const validIndicators = indicators.filter(ind => ind.percentile !== null);
    if (validIndicators.length === 0) return null;
    const sum = validIndicators.reduce((acc, ind) => acc + (ind.percentile || 0), 0);
    return Math.round(sum / validIndicators.length);
  };

  // Helper function to get percentile for an indicator, using CES4 data if available. Returns null when loading or no data.
  const getIndicatorPercentile = (indicatorName: string): number | null => {
    if (!ces4Data) return null;
    const ces4Field = CES4_INDICATOR_MAP[indicatorName];
    if (ces4Field && ces4Data[ces4Field] !== null && ces4Data[ces4Field] !== undefined) {
      return Math.round(ces4Data[ces4Field] as number);
    }
    return null;
  };

  const CATEGORY_INFO: Record<'burdens' | 'benefits', Record<string, string>> = {
    burdens: {
      'Greenhouse Gas Emissions (per capita)': 'Per-capita emissions from transportation, energy use, and waste.',
      Pollution: 'Air and toxic pollution exposures that affect community health.',
      Health: 'Public health outcomes linked to environmental and social conditions.',
      Transportation: 'Travel burdens and traffic exposure from transportation systems.',
      'Built Environment': 'Housing, energy cost burden, heat exposure, and neighborhood conditions.'
    },
    benefits: {
      Equity: 'Equity outcomes across income, education, and wages by race.',
      Socioeconomic: 'Economic stability and access to basic resources.',
      Mobility: 'Access to clean transportation options and infrastructure.',
      'Built Environment': 'Clean building technologies and affordable housing access.',
      'Community Resources': 'Access to green space and community amenities.'
    }
  };

  // Indicator name -> source key (for showing loading vs "Coming soon")
  const getSourceForIndicator = (indicatorName: string): keyof typeof sourcesLoaded | null => {
    const map: Record<string, keyof typeof sourcesLoaded> = {
      'Light-Duty Vehicles (LDV)': 'ghg', 'Heavy-Duty Vehicles (HDV)': 'ghg', 'Electricity': 'ghg', 'Natural Gas': 'ghg', 'Waste': 'ghg', 'Offroad': 'ghg',
      'Diesel PM': 'ces4', 'PM2.5': 'ces4', 'Ozone': 'ces4', 'Toxic Releases': 'ces4', 'Hazardous Waste': 'ces4',
      'Asthma Rate': 'ces4', 'Cardiovascular Disease': 'ces4', 'Low Birth Weight': 'ces4', 'Traffic': 'ces4', 'Traffic Density': 'ces4',
      'Commute Time': 'commute',
      "EV's per 1000 People": 'evPer1000', 'EV Charging': 'evCharging', 'Solar PV': 'solarPV', 'Heat Pump Adoption': 'heatPump',
      'Tree Canopy Coverage': 'treeCanopy',
      'Income Equity by Race': 'equity', 'Educational Attainment Equity by Race': 'equity', 'Wage Equity by Sector': 'equity',
      'Racial Equity (% Non-White)': 'racialNonWhite',
      'Median Household Income': 'equity', 'Job Wages': 'equity', 'Educational Attainment': 'educational', 'Health Insurance Coverage': 'healthInsurance',
    };
    return map[indicatorName] ?? null;
  };

  // Base categories: use null when source hasn't loaded so we show loading bar instead of default 50
  const baseCategories: Omit<Category, 'percentile'>[] = type === 'burdens' ? [
    {
      name: 'Greenhouse Gas Emissions (per capita)',
      expanded: false,
      indicators: [
        { name: 'Light-Duty Vehicles (LDV)', percentile: sourcesLoaded.ghg ? (ghgPercentiles?.ldv ?? null) : null, year: ghgYear },
        { name: 'Heavy-Duty Vehicles (HDV)', percentile: sourcesLoaded.ghg ? (ghgPercentiles?.hdv ?? null) : null, year: ghgYear },
        { name: 'Electricity', percentile: sourcesLoaded.ghg ? (ghgPercentiles?.electricity ?? null) : null, year: ghgYear },
        { name: 'Natural Gas', percentile: sourcesLoaded.ghg ? (ghgPercentiles?.naturalGas ?? null) : null, year: ghgYear },
        { name: 'Waste', percentile: sourcesLoaded.ghg ? (ghgPercentiles?.waste ?? null) : null, year: ghgYear },
        { name: 'Offroad', percentile: sourcesLoaded.ghg ? (ghgPercentiles?.offroad ?? null) : null, year: ghgYear }
      ]
    },
    {
      name: 'Pollution',
      expanded: false,
      indicators: [
        { name: 'Diesel PM', percentile: getIndicatorPercentile('Diesel PM') },
        { name: 'PM2.5', percentile: getIndicatorPercentile('PM2.5') },
        { name: 'Ozone', percentile: getIndicatorPercentile('Ozone') },
        { name: 'Toxic Releases', percentile: getIndicatorPercentile('Toxic Releases') },
        { name: 'Hazardous Waste', percentile: getIndicatorPercentile('Hazardous Waste') }
      ]
    },
    {
      name: 'Health',
      expanded: false,
      indicators: [
        { name: 'Asthma Rate', percentile: getIndicatorPercentile('Asthma Rate') },
        { name: 'Cancer Fatalities', percentile: null }, // Coming soon
        { name: 'Cardiovascular Disease', percentile: getIndicatorPercentile('Cardiovascular Disease') },
        { name: 'Low Birth Weight', percentile: getIndicatorPercentile('Low Birth Weight') },
        { name: 'Adult Depression', percentile: null } // Coming soon
      ]
    },
    {
      name: 'Transportation',
      expanded: false,
      indicators: [
        { name: 'Commute Time', percentile: commuteTimePercentile ?? null, year: commuteYear },
        { name: 'Traffic', percentile: getIndicatorPercentile('Traffic Density') }
      ]
    },
    {
      name: 'Built Environment',
      expanded: false,
      indicators: [
        { name: 'Housing Burden', percentile: housingBurdenPercentile !== null && housingBurdenPercentile !== undefined ? Math.round(housingBurdenPercentile) : null, year: null },
        { name: 'Energy Cost Burden', percentile: null }, // Coming soon
        { name: 'Percent Fossil Heating', percentile: null }, // Coming soon
        { name: 'Urban Heat Island', percentile: null }, // Coming soon
        { name: 'Displacement Risk', percentile: null }, // Coming soon
        { name: 'Crime and Safety Risks', percentile: null } // Coming soon
      ]
    }
  ] : [
    {
      name: 'Equity',
      expanded: false,
      indicators: [
        { name: 'Income Equity by Race', percentile: equityPercentiles?.incomeEquity ?? null, year: equityYears?.incomeEquity ?? null },
        { name: 'Educational Attainment Equity by Race', percentile: equityPercentiles?.educationEquity ?? null, year: equityYears?.educationEquity ?? null },
        { name: 'Wage Equity by Sector', percentile: equityPercentiles?.wageEquity ?? null, year: equityYears?.wageEquity ?? null },
        { name: 'Racial Equity (% Non-White)', percentile: racialNonWhitePercentile ?? null, year: racialNonWhiteYear }
      ]
    },
    {
      name: 'Socioeconomic',
      expanded: false,
      indicators: [
        { name: 'Median Household Income', percentile: medianIncomePercentile ?? null, year: equityYears?.medianIncome ?? null },
        { name: 'Employment Rate', percentile: null }, // Coming soon
        { name: 'Educational Attainment', percentile: educationalAttainmentPercentile ?? null, year: educationalYear },
        { name: 'Health Insurance Coverage', percentile: healthInsurancePercentile ?? null, year: healthInsuranceYear },
        { name: 'Housing Affordability', percentile: null }, // Coming soon
        { name: 'Job Wages', percentile: jobWagePercentile ?? null, year: equityYears?.jobWage ?? null }
      ]
    },
    {
      name: 'Mobility',
      expanded: false,
      indicators: [
        { name: 'Biking Infrastructure', percentile: null }, // Coming soon
        { name: 'Public Transit', percentile: null }, // Coming soon
        { name: "EV's per 1000 People", percentile: evPer1000Percentile ?? null, year: evPer1000Year },
        { name: 'EV Charging', percentile: evChargingPercentile ?? null, year: evChargingYear },
        { name: 'Walkscore', percentile: null } // Coming soon
      ]
    },
    {
      name: 'Built Environment',
      expanded: false,
      indicators: [
        { name: 'Affordable Housing', percentile: null }, // Coming soon
        { name: 'Solar PV', percentile: solarPVPercentile ?? null, year: solarPVYear },
        { name: 'Heat Pump Adoption', percentile: heatPumpPercentile ?? null, year: heatPumpYear }
      ]
    },
    {
      name: 'Community Resources',
      expanded: false,
      indicators: [
        { name: 'Tree Canopy Coverage', percentile: treeCanopyPercentile ?? null, year: treeCanopyYear },
        { name: 'Open Space Access', percentile: null }, // Coming soon
        { name: 'Public Safety', percentile: null } // Coming soon
      ]
    }
  ];

  // Calculate category percentiles as averages of their indicators
  const categories: Category[] = baseCategories.map(cat => ({
    ...cat,
    percentile: calculateCategoryPercentile(cat.indicators)
  }));

  // Calculate overall percentile as average of non-null categories and notify parent (null when no data)
  const totalScore: number | null = (() => {
    const withValues = categories.filter(cat => cat.percentile !== null);
    if (withValues.length === 0) return null;
    return Math.round(
      withValues.reduce((sum, cat) => sum + (cat.percentile ?? 0), 0) / withValues.length
    );
  })();

  const totalScoreYear: number | null = (() => {
    const years = categories
      .flatMap((category) => category.indicators)
      .filter((indicator) => indicator.percentile !== null && indicator.year !== null && indicator.year !== undefined)
      .map((indicator) => indicator.year as number);
    if (years.length === 0) return null;
    const counts = new Map<number, number>();
    years.forEach((year) => counts.set(year, (counts.get(year) || 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return b[0] - a[0];
    })[0][0];
  })();

  useEffect(() => {
    if (onPercentileCalculated && totalScore !== null) {
      onPercentileCalculated(totalScore);
    }
  }, [totalScore, onPercentileCalculated]);

  useEffect(() => {
    if (onScoreYearCalculated) {
      onScoreYearCalculated(totalScoreYear);
    }
  }, [totalScoreYear, onScoreYearCalculated]);

  const handleCategoryClick = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    setExpandedCategories(new Set(categories.map(cat => cat.name)));
  };

  const handleCollapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Map indicator names to their corresponding routes
  const getIndicatorRoute = (indicatorName: string): string | null => {
    const routeMap: { [key: string]: string } = {
      // Burdens - Transportation
      'Light-Duty Vehicles (LDV)': '/transportation',
      'Heavy-Duty Vehicles (HDV)': '/transportation',
      'Commute Time': '/transportation',
      'Traffic': '/transportation',
      // Burdens - Buildings/Energy
      'Electricity': '/buildings',
      'Natural Gas': '/buildings',
      'Energy Cost Burden': '/buildings',
      'Percent Fossil Heating': '/buildings',
      // Burdens - GHG
      'Waste': '/other',
      'Offroad': '/ghg',
      // Burdens - Built Environment
      'Housing Burden': '/people',
      // Burdens - Health
      'Asthma Rate': '/people',
      'Cardiovascular Disease': '/people',
      'Low Birth Weight': '/people',
      // Benefits - Transportation/Mobility
      "EV's per 1000 People": '/transportation',
      'EV Charging': '/transportation',
      'Public Transit': '/transportation',
      'Biking Infrastructure': '/transportation',
      // Benefits - Buildings
      'Solar PV': '/buildings',
      'Heat Pump Adoption': '/buildings',
      // Benefits - People/Socioeconomic
      'Income Equity by Race': '/people',
      'Racial Equity (% Non-White)': '/people',
      'Educational Attainment Equity by Race': '/people',
      'Wage Equity by Sector': '/people',
      'Median Household Income': '/people',
      'Employment Rate': '/people',
      'Educational Attainment': '/people',
      'Health Insurance Coverage': '/people',
      'Housing Affordability': '/people',
      'Job Wages': '/people',
    };
    return routeMap[indicatorName] || null;
  };

  // Map indicator names to section anchor ids (for deep-linking to specific charts)
  const getIndicatorHash = (indicatorName: string): string | null => {
    const hashMap: { [key: string]: string } = {
      'Housing Burden': 'chart-housing-burden',
      'Housing Affordability': 'chart-housing-burden',
      'Asthma Rate': 'chart-health',
      'Cardiovascular Disease': 'chart-health',
      'Low Birth Weight': 'chart-health',
      'Income Equity by Race': 'chart-income-poverty-by-race',
      'Racial Equity (% Non-White)': 'chart-income-poverty-by-race',
      'Median Household Income': 'chart-income-poverty-by-race',
      'Educational Attainment Equity by Race': 'chart-educational-attainment-by-race',
      'Educational Attainment': 'chart-educational-attainment-by-race',
      'Wage Equity by Sector': 'chart-job-wages',
      'Job Wages': 'chart-job-wages',
      'Health Insurance Coverage': 'chart-health-insurance',
      'Employment Rate': 'chart-employment',
    };
    return hashMap[indicatorName] || null;
  };

  const handleIndicatorClick = (indicatorName: string) => {
    const route = getIndicatorRoute(indicatorName);
    if (route) {
      const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
      const hash = getIndicatorHash(indicatorName);
      const path = slug ? `/${slug}${route}` : route;
      const fullPath = hash ? `${path}#${hash}` : path;
      navigate(fullPath);
      if (!hash) {
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      }
    }
  };

  // Loading bar (skeleton) for bars that haven't loaded yet
  const LoadingBar: React.FC<{ width?: string; height?: string; style?: React.CSSProperties }> = ({ width = '100%', height = '24px', style = {} }) => (
    <div
      style={{
        width,
        height,
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden',
        position: 'relative',
        ...style
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '50%',
          height: '100%',
          backgroundColor: '#d1d5db',
          borderRadius: '4px'
        }}
      />
    </div>
  );

  const getColor = (percentile: number) => {
    if (type === 'burdens') {
      // Burdens: Green (low) → Yellow → Red (high) - quintiles
      // Lower percentile = better (green), Higher percentile = worse (red)
      if (percentile < 20) return '#10b981';      // Green (0-20)
      if (percentile < 40) return '#34d399';     // Light Green (20-40)
      if (percentile < 60) return '#fbbf24';     // Yellow (40-60)
      if (percentile < 80) return '#f59e0b';     // Orange (60-80)
      return '#dc2626';                           // Red (80-100)
    } else {
      // Benefits: Red (low) → Yellow → Green (high) - quintiles
      // Lower percentile = worse (red), Higher percentile = better (green)
      if (percentile < 20) return '#dc2626';      // Red (0-20)
      if (percentile < 40) return '#f59e0b';      // Orange (20-40)
      if (percentile < 60) return '#fbbf24';     // Yellow (40-60)
      if (percentile < 80) return '#34d399';     // Light Green (60-80)
      return '#10b981';                           // Green (80-100)
    }
  };

  // Get label for percentile range
  const getLabel = (percentile: number) => {
    const rounded = Math.round(percentile);
    if (rounded < 20) return 'Very Low';
    if (rounded < 40) return 'Low';
    if (rounded >= 40 && rounded < 60) {
      // For average range, show "Less than Average" or "More than Average"
      // unless it's exactly 50% (rounded), which is just "Average"
      if (rounded === 50) return 'Average';
      return rounded < 50 ? 'Less than Average' : 'More than Average';
    }
    if (rounded < 80) return 'High';
    return 'Very High';
  };

  return (
    <div style={{ width: '100%', padding: '0' }}>
      {/* Total Score - Full Width */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
            {`Total Score${totalScoreYear ? ` (${totalScoreYear})` : ''}:`}
          </span>
          {totalScore !== null ? (
            <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
              {totalScore}% ({getLabel(totalScore)})
            </span>
          ) : (
            <span style={{ fontSize: '14px', color: '#9ca3af', fontStyle: 'italic' }}>Loading…</span>
          )}
        </div>
        {totalScore !== null ? (
          <div style={{ width: '100%', height: '24px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
            <div
              style={{
                width: `${totalScore}%`,
                height: '100%',
                backgroundColor: getColor(totalScore),
                transition: 'width 0.3s ease'
              }}
            />
            <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
              {totalScore}%
            </span>
          </div>
        ) : (
          <LoadingBar width="100%" height="24px" />
        )}
        {/* Quintile Labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '12px' }}>
          <span style={{ color: type === 'burdens' ? '#10b981' : '#dc2626', fontWeight: '500' }}>Very Low</span>
          <span style={{ color: type === 'burdens' ? '#34d399' : '#f59e0b', fontWeight: '500' }}>Low</span>
          <span style={{ color: '#fbbf24', fontWeight: '500' }}>Average</span>
          <span style={{ color: type === 'burdens' ? '#f59e0b' : '#34d399', fontWeight: '500' }}>High</span>
          <span style={{ color: type === 'burdens' ? '#dc2626' : '#10b981', fontWeight: '500' }}>Very High</span>
        </div>
      </div>
      
      {/* Expand/Collapse Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={handleExpandAll}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#3b82f6',
            backgroundColor: 'white',
            border: '1px solid #3b82f6',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#eff6ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Expand
        </button>
        <button
          onClick={handleCollapseAll}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#6b7280',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Collapse
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {categories.map((category) => {
          const isExpanded = expandedCategories.has(category.name);
          const indicatorList = category.indicators.map(indicator => indicator.name).join(', ');
          const categoryDescription = CATEGORY_INFO[type][category.name];
          const categoryTooltip = `${categoryDescription} Indicators: ${indicatorList}`;
          return (
            <div key={category.name} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
              <div
                onClick={() => handleCategoryClick(category.name)}
                title={categoryTooltip}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  backgroundColor: isExpanded ? '#f3f4f6' : 'transparent',
                  borderRadius: '4px'
                }}
              >
                <span style={{ fontWeight: '600', fontSize: '14px' }}>{category.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {category.percentile !== null ? (
                    <div style={{ width: '200px', height: '24px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${category.percentile}%`,
                          height: '100%',
                          backgroundColor: getColor(category.percentile),
                          transition: 'width 0.3s ease'
                        }}
                      />
                      <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
                        {category.percentile}%
                      </span>
                    </div>
                  ) : (
                    <LoadingBar width="200px" height="24px" />
                  )}
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                </div>
              </div>
              {isExpanded && (
                <div style={{ marginTop: '12px', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {category.indicators.map((indicator) => {
                      const hasData = indicator.percentile !== null;
                      const percentile = indicator.percentile as number | null;
                      const route = getIndicatorRoute(indicator.name);
                      const isClickable = route !== null;
                      return (
                        <div key={indicator.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px' }}>
                          <span 
                            onClick={() => isClickable && handleIndicatorClick(indicator.name)}
                            style={{ 
                              fontSize: '12px', 
                              color: isClickable ? '#2563eb' : '#6b7280',
                              cursor: isClickable ? 'pointer' : 'default',
                              textDecoration: isClickable ? 'underline' : 'none',
                              textDecorationColor: isClickable ? '#2563eb' : 'transparent',
                              textUnderlineOffset: '2px'
                            }}
                            title={isClickable ? `Click to view ${indicator.name} chart` : undefined}
                          >
                            {`${indicator.name}${hasData && indicator.year && totalScoreYear && indicator.year !== totalScoreYear ? ` (${indicator.year})` : ''}`}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {hasData && percentile !== null ? (
                              <div style={{ width: '150px', height: '20px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${percentile}%`,
                                    height: '100%',
                                    backgroundColor: getColor(percentile),
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '10px', fontWeight: '600', color: '#374151' }}>
                                  {percentile}%
                                </span>
                              </div>
                            ) : (() => {
                              const source = getSourceForIndicator(indicator.name);
                              const isLoading = source !== null && !sourcesLoaded[source];
                              return isLoading ? (
                                <LoadingBar width="150px" height="20px" />
                              ) : (
                                <span style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' }}>
                                  Coming soon
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
